import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  Avatar,
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { userService, uploadService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../utils/toast';

const ProfilePictureDialog = ({ open, onClose }) => {
  const auth = useAuth();
  const { user, setUser } = auth;
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showToast.error('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast.error('Image size should be less than 5MB');
        return;
      }

      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showToast.error('Please select an image');
      return;
    }

    if (!user) {
      showToast.error('User information not available');
      return;
    }

    // Get user ID (could be 'id' or 'user_id' from JWT payload)
    const userId = user.id || user.user_id;
    if (!userId) {
      showToast.error('User ID not found');
      return;
    }

    try {
      setLoading(true);
      
      // Upload file to server (backend will automatically delete previous image and update user table)
      const uploadResponse = await uploadService.uploadFile(selectedFile);
      
      if (!uploadResponse.success) {
        showToast.error(uploadResponse.error || uploadResponse.message || 'Failed to upload image');
        return;
      }
      
      // Get the URL from response (response structure: { success: true, data: "url_string" } or direct URL)
      const imageUrl = uploadResponse.data || uploadResponse.url || (typeof uploadResponse === 'string' ? uploadResponse : null);
      
      if (!imageUrl) {
        showToast.error('No URL returned from upload');
        return;
      }
      
      // Backend already updated the user table, just update local state
      const updatedUser = { ...user, profile_image: imageUrl };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      showToast.success('Profile picture updated successfully');
      handleClose();
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      showToast.error(error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to update profile picture');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedFile(null);
      setPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  const handleRemove = async () => {
    if (!user) {
      showToast.error('User information not available');
      return;
    }

    // Get user ID (could be 'id' or 'user_id' from JWT payload)
    const userId = user.id || user.user_id;
    if (!userId) {
      showToast.error('User ID not found');
      return;
    }

    try {
      setLoading(true);
      
      // Remove profile_image by setting it to null
      const response = await userService.update(userId, {
        profile_image: null,
      });

      if (response.success || response.data) {
        // Update user in context
        const updatedUser = { ...user, profile_image: null };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        showToast.success('Profile picture removed successfully');
        handleClose();
      } else {
        showToast.error(response.message || 'Failed to remove profile picture');
      }
    } catch (error) {
      console.error('Error removing profile picture:', error);
      showToast.error(error?.response?.data?.error || error?.message || 'Failed to remove profile picture');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Profile Picture</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 2 }}>
          <Avatar
            src={preview || user?.profile_image}
            sx={{
              width: 120,
              height: 120,
              bgcolor: 'primary.main',
              fontSize: 48,
            }}
          >
            {!preview && !user?.profile_image && (user?.full_name?.charAt(0)?.toUpperCase() || 'U')}
          </Avatar>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            id="profile-picture-input"
          />
          
          <label htmlFor="profile-picture-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<PhotoCamera />}
              disabled={loading}
            >
              {preview ? 'Change Image' : 'Select Image'}
            </Button>
          </label>
          
          {selectedFile && (
            <Typography variant="body2" color="text.secondary">
              Selected: {selectedFile.name}
            </Typography>
          )}
          
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            Supported formats: JPG, PNG, GIF (Max 5MB)
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        {user?.profile_image && (
          <Button onClick={handleRemove} color="error" disabled={loading}>
            Remove
          </Button>
        )}
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={loading || !selectedFile}
        >
          {loading ? <CircularProgress size={20} /> : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfilePictureDialog;

